import { supabase } from './supabase';
import type { Company, SalesRep, Party, ItemCode, PriceListItem } from '../types';
import ExcelJS from 'exceljs';

export interface ImportResult {
  success: boolean;
  message: string;
  inserted?: number;
  errors?: string[];
}

function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

type InsertableCompany = Omit<Company, 'id'>;
type InsertableSalesRep = Omit<SalesRep, 'id'>;
type InsertableParty = Omit<Party, 'id'>;
type InsertableItemCode = Omit<ItemCode, 'id'>;
type InsertablePriceListItem = Omit<PriceListItem, 'id'>;

async function readFirstSheetRows(file: File): Promise<Record<string, any>[]> {
  const buffer = await file.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);
  const worksheet = workbook.getWorksheet(1);
  if (!worksheet) return [];

  const headers: string[] = [];
  worksheet.getRow(1).eachCell((cell) => {
    headers.push(String(cell.value || cell.address || '').trim());
  });

  const rows: Record<string, any>[] = [];
  let rowIndex = 0;
  worksheet.eachRow((row) => {
    rowIndex += 1;
    if (rowIndex === 1) return;
    const record: Record<string, any> = {};
    row.eachCell((cell, colNumber) => {
      const header = headers[colNumber - 1];
      if (!header) return;
      record[header] = cell.value;
    });
    rows.push(record);
  });

  return rows;
}

export async function importCompaniesFromExcel(file: File): Promise<ImportResult> {
  const rows = await readFirstSheetRows(file);
  const companies: Company[] = [];
  const errors: string[] = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    try {
      const company: Company = {
        code: String(row['Code'] || row['code'] || '').trim(),
        name: String(row['Name'] || row['name'] || '').trim(),
        gst: row['GST'] || row['gst'] || undefined,
        address: row['Address'] || row['address'] || undefined,
        phone: row['Phone'] || row['phone'] || undefined,
        email: row['Email'] || row['email'] || undefined,
        bank_name: row['Bank Name'] || row['bank_name'] || undefined,
        account_number: row['Account Number'] || row['account_number'] || undefined,
        ifsc_code: row['IFSC Code'] || row['ifsc_code'] || undefined,
        signatory: row['Signatory'] || row['signatory'] || undefined,
        hsn_codes: row['HSN Codes'] ? String(row['HSN Codes']).split(',').map((s: string) => s.trim()) : undefined,
      };

      if (!company.code || !company.name) {
        errors.push(`Row ${i + 2}: missing code or name`);
        continue;
      }

      companies.push(company);
    } catch (err) {
      errors.push(`Row ${i + 2}: ${err}`);
    }
  }

  if (companies.length === 0) {
    return { success: false, message: 'No valid companies found', errors };
  }

  const { error } = await supabase.from('companies').insert(companies as InsertableCompany[]);

  if (error) {
    return { success: false, message: error.message, errors: [error.message] };
  }

  return { success: true, message: `Imported ${companies.length} companies`, inserted: companies.length, errors };
}

export async function importSalesRepsFromExcel(file: File): Promise<ImportResult> {
  const rows = await readFirstSheetRows(file);
  const reps: SalesRep[] = [];
  const errors: string[] = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    try {
      const rep: SalesRep = {
        code: String(row['Code'] || row['code'] || '').trim(),
        name: String(row['Name'] || row['name'] || '').trim(),
        role: row['Role'] || row['role'] || undefined,
        company_ids: row['Company IDs'] || row['company_ids'] ? String(row['Company IDs'] || row['company_ids']).split(',').map((s: string) => parseInt(s.trim())).filter((n: number) => !isNaN(n)) : undefined,
        is_active: true,
      };

      if (!rep.code || !rep.name) {
        errors.push(`Row ${i + 2}: missing code or name`);
        continue;
      }

      reps.push(rep);
    } catch (err) {
      errors.push(`Row ${i + 2}: ${err}`);
    }
  }

  if (reps.length === 0) {
    return { success: false, message: 'No valid sales reps found', errors };
  }

  const { error } = await supabase.from('sales_reps').insert(reps as InsertableSalesRep[]);

  if (error) {
    return { success: false, message: error.message, errors: [error.message] };
  }

  return { success: true, message: `Imported ${reps.length} sales reps`, inserted: reps.length, errors };
}

export async function importPartiesFromExcel(file: File): Promise<ImportResult> {
  const rows = await readFirstSheetRows(file);
  const parties: Party[] = [];
  const errors: string[] = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    try {
      const party: Party = {
        name: String(row['Name'] || row['name'] || '').trim(),
        address1: row['Address 1'] || row['address1'] || undefined,
        address2: row['Address 2'] || row['address2'] || undefined,
        address3: row['Address 3'] || row['address3'] || undefined,
        contact_person: row['Contact Person'] || row['contact_person'] || undefined,
        phone: row['Phone Nos'] || row['Phone'] || row['phone'] || undefined,
        email: row['Email'] || row['email'] || undefined,
        gst: row['GST'] || row['gst'] || undefined,
        company_id: row['Company ID'] || row['company_id'] ? parseInt(String(row['Company ID'] || row['company_id'])) : undefined,
      };

      if (!party.name) {
        errors.push(`Row ${i + 2}: missing party name`);
        continue;
      }

      parties.push(party);
    } catch (err) {
      errors.push(`Row ${i + 2}: ${err}`);
    }
  }

  if (parties.length === 0) {
    return { success: false, message: 'No valid parties found', errors };
  }

  const batchSize = 500;
  let totalInserted = 0;

  for (let i = 0; i < parties.length; i += batchSize) {
    const batch = parties.slice(i, i + batchSize);
    const { error } = await supabase.from('parties').insert(batch as InsertableParty[]);

    if (error) {
      return { success: false, message: error.message, errors: [error.message, ...errors] };
    }

    totalInserted += batch.length;
    await sleep(300);
  }

  return { success: true, message: `Imported ${totalInserted} parties`, inserted: totalInserted, errors };
}

export async function importItemCodesFromExcel(file: File): Promise<ImportResult> {
  const rows = await readFirstSheetRows(file);
  const items: ItemCode[] = [];
  const errors: string[] = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    try {
      const item: ItemCode = {
        code: String(row['Code'] || row['code'] || '').trim(),
        description: row['Description'] || row['description'] || undefined,
        castor_type: row['Castor Type'] || row['castor_type'] || undefined,
        wheel_type: row['Wheel Type'] || row['wheel_type'] || undefined,
        wheel_material: row['Wheel Material'] || row['wheel_material'] || undefined,
        size: row['Size'] || row['size'] || undefined,
        bearing: row['Bearing'] || row['bearing'] || undefined,
        circlip: row['Circlip'] || row['circlip'] || undefined,
        bush: row['Bush'] || row['bush'] || undefined,
        thread_guard: row['Thread Guard'] || row['thread_guard'] || undefined,
        nut_bolt: row['Nut Bolt'] || row['nut_bolt'] || undefined,
        base_price: row['Base Price'] || row['base_price'] ? Number(row['Base Price'] || row['base_price']) : undefined,
        is_active: true,
      };

      if (!item.code) {
        errors.push(`Row ${i + 2}: missing item code`);
        continue;
      }

      items.push(item);
    } catch (err) {
      errors.push(`Row ${i + 2}: ${err}`);
    }
  }

  if (items.length === 0) {
    return { success: false, message: 'No valid item codes found', errors };
  }

  const { error } = await supabase.from('item_codes').insert(items as InsertableItemCode[]);

  if (error) {
    return { success: false, message: error.message, errors: [error.message] };
  }

  return { success: true, message: `Imported ${items.length} item codes`, inserted: items.length, errors };
}

export async function importPriceListFromExcel(file: File): Promise<ImportResult> {
  const rows = await readFirstSheetRows(file);
  const prices: PriceListItem[] = [];
  const errors: string[] = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    try {
      const price: PriceListItem = {
        category: String(row['Category'] || row['category'] || '').trim(),
        item_code: String(row['Item Code'] || row['item_code'] || '').trim(),
        item_name: row['Item Name'] || row['item_name'] || undefined,
        size: row['Size'] || row['size'] || undefined,
        price: Number(row['Price'] || row['price'] || 0),
        effective_date: row['Effective Date'] || row['effective_date'] || undefined,
      };

      if (!price.category || !price.item_code) {
        errors.push(`Row ${i + 2}: missing category or item code`);
        continue;
      }

      prices.push(price);
    } catch (err) {
      errors.push(`Row ${i + 2}: ${err}`);
    }
  }

  if (prices.length === 0) {
    return { success: false, message: 'No valid price list entries found', errors };
  }

  const { error } = await supabase.from('price_list').insert(prices as InsertablePriceListItem[]);

  if (error) {
    return { success: false, message: error.message, errors: [error.message] };
  }

  return { success: true, message: `Imported ${prices.length} price list entries`, inserted: prices.length, errors };
}

export async function importQuotationsFromExcel(_file: File): Promise<ImportResult> {
  return { success: false, message: 'Quotation import requires line items mapping. Use party/item/price imports first.' };
}
